import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/**
 * Evita colisiones de ID cuando el quick-add se dispara desde una página de
 * producto: renombra los id="" del contenido clonado y actualiza cualquier
 * on:click="#id/..." que los referencie, para que apunten al clon del modal
 * en vez de a elementos con el mismo id ya presentes en la página actual.
 * @param {Element} root
 */
function namespaceIds(root) {
  const suffix = '-qa-' + Math.random().toString(36).slice(2, 8);
  const idMap = new Map();

  root.querySelectorAll('[id]').forEach((el) => {
    const newId = el.id + suffix;
    idMap.set(el.id, newId);
    el.id = newId;
  });

  root.querySelectorAll('*').forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      if (!attr.name.startsWith('on:')) continue;
      const parts = attr.value.split('/');
      const selector = parts[0];
      if (selector.startsWith('#') && idMap.has(selector.slice(1))) {
        parts[0] = '#' + idMap.get(selector.slice(1));
        el.setAttribute(attr.name, parts.join('/'));
      }
    }
  });
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

        if (!productGrid) {
      // Fetch and cache the content. Si la primera petición vuelve vacía
      // (por ejemplo, cancelada por la precarga al pasar el mouse), reintenta
      // una vez más — la segunda casi siempre llega bien.
      let html = await this.fetchProductPage(currentUrl);
      if (!html) {
        html = await this.fetchProductPage(currentUrl);
      }
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      namespaceIds(freshContent);
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);

      const sourceProductCard = this.closest('product-card');
      const sourceWyshButton = sourceProductCard?.querySelector('.wysh-button');
      setupWyshProxy(document.getElementById('quick-add-modal-content'), sourceWyshButton);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

        morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);

        if (!isMobileBreakpoint()) {
      buildQuickAddGallery(modalContent);
      moveRatingIntoTitleRow(modalContent);
      fillEstimatedDelivery(modalContent);
      moveEstimatedDeliveryAboveButton(modalContent);
      addPolicyLinks(modalContent);
    }
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

function getModalSelectedColor(modalContent) {
  const checked = modalContent.querySelector('variant-picker input[type="radio"]:checked');
  return checked ? checked.value.trim().toLowerCase() : null;
}

function buildQuickAddGallery(modalContent) {
  const mediaContainer = modalContent.querySelector('.product-information__media');
  if (!mediaContainer) return;

  const nativeGallery = mediaContainer.querySelector('media-gallery');
  if (!nativeGallery) return;

  mediaContainer.querySelector('.lov-quick-add-gallery')?.remove();

  const slides = mediaContainer.querySelectorAll('slideshow-slide[data-color]');
  if (slides.length === 0) return;

  const gallery = document.createElement('div');
  gallery.className = 'lov-quick-add-gallery';

  slides.forEach((slide) => {
    const img = slide.querySelector('img');
    if (!img) return;
    const clone = img.cloneNode(true);
    clone.classList.add('lov-quick-add-gallery__img');
    clone.dataset.lovColor = slide.dataset.color.trim().toLowerCase();
    gallery.appendChild(clone);
  });

    mediaContainer.insertBefore(gallery, nativeGallery);
  updateQuickAddGallery(modalContent);
  setupCustomScrollbar(modalContent, gallery);
}

function setupCustomScrollbar(modalContent, gallery) {
  modalContent.querySelector('.lov-quick-add-gallery-scrollbar')?.remove();

  const bar = document.createElement('div');
  bar.className = 'lov-quick-add-gallery-scrollbar';
  bar.innerHTML = '<div class="lov-quick-add-gallery-scrollbar__track"></div><div class="lov-quick-add-gallery-scrollbar__thumb"></div>';
  modalContent.appendChild(bar);

  const thumb = bar.querySelector('.lov-quick-add-gallery-scrollbar__thumb');

  function update() {
    const { scrollTop, scrollHeight, clientHeight } = gallery;
    if (scrollHeight <= clientHeight) {
      bar.style.display = 'none';
      return;
    }
    bar.style.display = 'block';
    const thumbHeight = Math.max((clientHeight / scrollHeight) * clientHeight, 24);
    const maxThumbTop = clientHeight - thumbHeight;
    const thumbTop = (scrollTop / (scrollHeight - clientHeight)) * maxThumbTop;
    thumb.style.height = `${thumbHeight}px`;
    thumb.style.top = `${thumbTop}px`;
  }

  gallery.addEventListener('scroll', update);
  update();
}

function updateQuickAddGallery(modalContent) {
  const gallery = modalContent.querySelector('.lov-quick-add-gallery');
  if (!gallery) return;

  const selectedColor = getModalSelectedColor(modalContent);
  const imgs = gallery.querySelectorAll('.lov-quick-add-gallery__img');
  let anyMatch = false;
  imgs.forEach((img) => {
    if (selectedColor && img.dataset.lovColor === selectedColor) anyMatch = true;
  });

  imgs.forEach((img) => {
    const isMatch = anyMatch ? img.dataset.lovColor === selectedColor : true;
    img.style.display = isMatch ? 'block' : 'none';
  });
}

document.addEventListener('change', (event) => {
  if (isMobileBreakpoint()) return;
  const modalContent = document.getElementById('quick-add-modal-content');
  if (!modalContent || !modalContent.contains(event.target)) return;
  if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;
  updateQuickAddGallery(modalContent);
});

/**
 * Clona visualmente el botón real de Wysh (de la tarjeta que abrió el modal)
 * y lo coloca junto al título dentro del modal. Al hacer clic en el clon,
 * dispara el clic del botón real — toda la lógica de guardar sigue siendo
 * de Wysh, nosotros solo reenviamos el evento y copiamos su apariencia.
 * @param {Element | null} modalContent
 * @param {Element | null | undefined} sourceButton
 */
function setupWyshProxy(modalContent, sourceButton) {
  if (!modalContent) return;

  const titleRow = modalContent.querySelector('.view-product-title');
  titleRow?.querySelector('.lov-wysh-proxy')?.remove();

  if (!titleRow || !sourceButton) return;

  const proxy = sourceButton.cloneNode(true);
  if (!(proxy instanceof HTMLElement)) return;

  proxy.classList.add('lov-wysh-proxy');
  proxy.removeAttribute('id');

  proxy.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (sourceButton instanceof HTMLElement) sourceButton.click();
  });

  titleRow.appendChild(proxy);

  const syncState = () => {
    proxy.className = sourceButton.className + ' lov-wysh-proxy';
  };
  syncState();

  const observer = new MutationObserver(syncState);
  observer.observe(sourceButton, { attributes: true, attributeFilter: ['class'] });
}

function moveRatingIntoTitleRow(modalContent) {
  const price = modalContent.querySelector('.product-details product-price');
  const rating = modalContent.querySelector('.product-details .rating-wrapper');
  if (!price || !rating) return;

  const priceRow = price.parentElement;
  priceRow.classList.add('lov-price-row');
  rating.classList.add('lov-rating-in-title');
  priceRow.appendChild(rating);
}

function fillEstimatedDelivery(modalContent) {
  const el = modalContent.querySelector('.estimated-delivery');
  if (!el) return;

  const prefixEl = el.querySelector('.estimated-delivery__prefix');
  const dateEl = el.querySelector('.estimated-delivery__date');
  if (!prefixEl || !dateEl) return;

  const BUSINESS_DAYS_TO_ADD = 5;

  function addBusinessDays(startDate, daysToAdd) {
    const result = new Date(startDate);
    let added = 0;
    while (added < daysToAdd) {
      result.setDate(result.getDate() + 1);
      const dayOfWeek = result.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        added++;
      }
    }
    return result;
  }

  const today = new Date();
  const deliveryDate = addBusinessDays(today, BUSINESS_DAYS_TO_ADD);

  const weekday = deliveryDate.toLocaleDateString('en-US', { weekday: 'long' });
  const month = deliveryDate.toLocaleDateString('en-US', { month: 'long' });
  const dayNumber = deliveryDate.getDate();

  prefixEl.textContent = 'Estimated delivery for';
  dateEl.textContent = `${weekday}, ${month} ${dayNumber}`;
}

function moveEstimatedDeliveryAboveButton(modalContent) {
  const delivery = modalContent.querySelector('.estimated-delivery');
  const buyButtons = modalContent.querySelector('.buy-buttons-block');
  if (!delivery || !buyButtons) return;

  delivery.classList.add('lov-estimated-delivery-visible');
  buyButtons.parentElement.insertBefore(delivery, buyButtons);
}

function addPolicyLinks(modalContent) {
  const buyButtons = modalContent.querySelector('.buy-buttons-block');
  if (!buyButtons) return;
  if (modalContent.querySelector('.lov-policy-links')) return;

  const row = document.createElement('div');
  row.className = 'lov-policy-links';
  row.innerHTML =
    '<a href="/pages/size-guide" target="_blank" rel="noopener">Size Guide</a>' +
    '<a href="/policies/shipping-policy" target="_blank" rel="noopener">Shipping Policy</a>';

  buyButtons.parentElement.insertBefore(row, buyButtons);
}